import { useEffect, useState, useMemo, useRef } from 'react';
import SearchService from '../../services/search.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import CompanyServices from '../../services/company.services';
import {
	FluxChip,
	FluxButton,
	FluxCircularProgress,
	FluxList,
	FluxListItem,
	FluxListSubheader,
} from '@meltwater/flux-web-components-react';
import csvDownload from 'json-to-csv-export';
import { Box, Stack } from '@mui/material';
import styles from './search.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { saveAs } from 'file-saver';
import ConfirmationDialog from '../../components/confirmationdialog';
import { useSearchData } from './searchexports';
import DataTable from '../../components/DataTable';
import { useSearchPages } from './useSearchPages';

const Search = ({ load, setActiveTab }) => {
	const { searchData: cachedSearchData, setSearchData: setCachedSearchData } = useSearchData();
	const [users, setUsers] = useState([]);
	const [permissions, setPermssions] = useState([]);
	const [progress, setProgress] = useState(0);
	const [validating, setValidating] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);
	const [openSnackBar, setOpenSnackBar] = useState(false);
	const [feedbackMsg, setFeedbackMsg] = useState('');
	const [severity, setSeverity] = useState('info');

	const [exporting, setExporting] = useState(false);
	const [exportMessage, setExportMessage] = useState('');

	const stopValidationRef = useRef(false);
	const exportAbortRef = useRef(null);
	const { companyId } = useCompanyId();

	// The searches endpoint is cursor-paginated, and the table drives it: one
	// page on screen is one request, so a large company is never pulled into the
	// browser in full. See useSearchPages for the cursor bookkeeping.
	const {
		rows: searchdata,
		hasLoaded,
		pagination,
		onPaginationChange,
		rowCount,
		hasMore,
		isLoading,
		isFetching,
		error: loadError,
		refresh: refreshSearches,
		pageSizeOptions,
	} = useSearchPages(companyId, {
		enabled: load,
		cache: cachedSearchData,
		setCache: setCachedSearchData,
	});

	// Selection is keyed by search id and survives paging, so the row ids the
	// table knows about (the current page) are not the whole selection.
	const [rowSelection, setRowSelection] = useState({});
	const [selectedSearches, setSelectedSearches] = useState([]);

	// A failure with nothing on screen is a dead end and gets the error page; one
	// while a page is already displayed is reported without throwing it away.
	const hasError = Boolean(loadError) && !hasLoaded;
	const errorMessage = loadError || '';
	
	// Custom guide steps specific to Search component
	const searchGuideSteps = useMemo(() => [
		{
			title: 'Search Management',
			text: 'This guide will help you navigate the Search Management features.',
			element: null,
		},
		{
			title: 'Search Queries',
			text: 'Filter the searches on the current page by name, ID, or content. Use the pager below to move through the rest.',
			element: '.mrt-input',
			position: { top: 20, left: 200 }
		},
		{
			title: 'Column Visibility & Table Settings',
			text: 'Use these buttons to show or hide specific columns and adjust table settings.',
			element: '.mrt-button.mrt-dropdown-button',
			position: { top: 0, right: 80 }
		},
		{
			title: 'Export All',
			text: 'Export all search queries with their configuration details and metadata.',
			element: '.MuiButtonBase-root.FluxButton-root',
			position: { top: 0, left: 100 }
		},
		{
			title: 'Export Selected',
			text: 'Export only selected search queries with their configuration details.',
			element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(2)',
			position: { top: 0, left: 200 }
		},
		{
			title: 'Export Word',
			text: 'Export selected searches in Microsoft Word format for easier reading and sharing.',
			element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(3)',
			position: { top: 0, left: 300 }
		},
		{
			title: 'Run Validation',
			text: 'Validate selected searches to check for errors or issues with their configuration.',
			element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(4)',
			position: { top: 0, left: 400 }
		},
		{
			title: 'Validation Information',
			text: 'Search validation checks for syntax errors and other issues. It takes approximately 1.5 seconds per search and will generate a detailed report.',
			element: null,
		},
		{
			title: 'Refresh Data',
			text: 'Use this button to refresh search data from the server.',
			element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(5)',
			position: { top: 0, left: 500 }
		},
		{
			title: 'Search Selection',
			text: 'Select searches with checkboxes to perform operations on specific searches.',
			element: '.mrt-row-select-checkbox',
			position: { top: 100, left: 30 }
		},
		{
			title: 'Query Types',
			text: 'The Query Type column shows whether a search uses keyword, boolean, or combined search methods.',
			element: '.mrt-cell:nth-of-type(3)',
			position: { top: 100, left: 300 }
		},
		{
			title: 'Labels',
			text: 'The Labels column shows tags associated with each search for easier organization.',
			element: '.mrt-cell:nth-of-type(4)',
			position: { top: 100, left: 400 }
		}
	], []);
	
	const handleOpenDialog = () => {
		setOpenDialog(true);
	};
	const handleStopValidation = () => {
		stopValidationRef.current = true;
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};
	
	const handleCloseSnackBar = () => {
		setOpenSnackBar(false);
	};
	
	const handleConfirmAction = (rows) => {
		validate(rows);
		handleCloseDialog();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Name',
				accessorKey: 'name', //simple accessorKey pointing to flat data
				enableColumnFilterModes: false, //disable changing filter mode for this column
				filterFn: 'contains', //even though changing the mode is disabled, you can still set the default filter mode
			},
			{
				header: 'id',
				accessorKey: 'id', //simple accessorKey pointing to flat data
			},
			{
				header: 'Query Type',
				accessorFn: (row) => `${row.searchQuery.searchQueryType}`,
				filterVariant: 'select',
				filterSelectOptions: ['keyword', 'boolean', 'combined'],
			},
			{
				header: 'Labels',
				accessorFn: (row) => `${row.labelNames.join(', ')}`,
				enableColumnFilterModes: false,
				filterFn: 'contains',
			},
			{
				header: 'AnySearches',
				size: 250,
				Cell: ({ renderedCellValue, row }) => (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'left',
							gap: '1rem',
						}}
					>
						<span>
							{row.original.searchQuery.anySearches.map((itm) => {
								return (
									<FluxChip displayOnly dense>
										{' '}
										{itm.id}
									</FluxChip>
								);
							})}
						</span>
					</Box>
				),
			},
			{
				header: 'AllSearches',
				size: 250,
				Cell: ({ renderedCellValue, row }) => (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'left',
							gap: '1rem',
						}}
					>
						<span>
							{row.original.searchQuery.allSearches.map((itm) => {
								return (
									<FluxChip displayOnly dense>
										{' '}
										{itm.id}
									</FluxChip>
								);
							})}
						</span>
					</Box>
				),
			},
			{
				header: 'NotSearches',
				size: 250,
				Cell: ({ renderedCellValue, row }) => (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'left',
							gap: '1rem',
						}}
					>
						<span>
							{row.original.searchQuery.notSearches.map((itm) => {
								return (
									<FluxChip displayOnly dense>
										{' '}
										{itm.id}
									</FluxChip>
								);
							})}
						</span>
					</Box>
				),
			},
			{
				header: 'Boolean',
				accessorFn: (row) => `${row.searchQuery.booleanQuery}`,
				size: 180,
			},
			{
				header: 'Custom Category',
				accessorFn: (row) =>
					`${row.searchQuery.filter.filters.customCategory.map(
						(category) => `${category.id}-${category.name}`
					)}`,
				size: 180,
			},
		],
		[]
	);
	
	// The company's users are only needed to resolve createdBy/updatedBy in the
	// CSV export, so they are fetched once per company rather than per page, and
	// a failure degrades those two columns instead of the whole table.
	useEffect(() => {
		if (!load || !companyId) return undefined;

		let cancelled = false;
		CompanyServices.getCompanyUsers(companyId)
			.then((result) => {
				if (!cancelled) setUsers(result.data);
			})
			.catch((err) => {
				console.log('Search: could not load company users', err.message);
			});

		return () => {
			cancelled = true;
		};
	}, [load, companyId]);

	// A page that failed to load while earlier pages are on screen: say so,
	// leaving what the user is looking at in place.
	useEffect(() => {
		if (loadError && hasLoaded) {
			setFeedbackMsg(`Error loading searches: ${loadError}`);
			setSeverity('error');
			setOpenSnackBar(true);
		}
	}, [loadError, hasLoaded]);

	// Rows selected on a page the user has since left are still part of the
	// selection, but the table only holds the current page — so the chosen
	// searches are accumulated here and the exports work off this list.
	useEffect(() => {
		setSelectedSearches((previous) => {
			const byId = new Map(previous.map((search) => [String(search.id), search]));
			byId.forEach((_value, id) => {
				if (!rowSelection[id]) byId.delete(id);
			});
			Object.entries(rowSelection).forEach(([id, isSelected]) => {
				if (!isSelected || byId.has(id)) return;
				const search = searchdata.find((row) => String(row.id) === id);
				if (search) byId.set(id, search);
			});
			return Array.from(byId.values());
		});
	}, [rowSelection, searchdata]);

	// The export/validation helpers below were written against the table's row
	// model, so the accumulated selection is handed over in the same shape.
	const selectedRows = useMemo(
		() => selectedSearches.map((search) => ({ original: search })),
		[selectedSearches]
	);

	function getOneSearch(search) {
		return `
    <tr>
    <td>${search.id}
    <td>${search.name}
    <td>${search.searchQuery.booleanQuery}</td>
    <td> ${search.searchQuery.informationTypes.map((itm) => {
					return itm;
				})}</td>
    </tr>`;
	}

	const validate = async (rows) => {
		const finalexport = [];
		setValidating(true);

		for (const [index, oneitem] of rows.entries()) {
			console.log('validate-stopValidation', stopValidationRef.current);
			if (stopValidationRef.current) break;

			try {
				setProgress((prevProgress) => {
					const newProgress = Math.round((index / rows.length) * 100);
					return newProgress;
				});

				const myres = await SearchService.validateSearch(
					companyId,
					oneitem.original.id
				);

				const response = {
					companyId: companyId,
					searchname: oneitem.original.name,
					searchid: oneitem.original.id,
					status: myres.data.results.status,
					errors:
						myres.data.results.errors.map((oneitem) => `"${oneitem.message}"`) || '',
					booleanQuery: oneitem.original.searchQuery.booleanQuery || '',
					allKeywords: oneitem.original.searchQuery.allKeywords.toString() || '',
					allSearches:
						oneitem.original.searchQuery.allSearches.map(
							(oneitem) => `"${oneitem.id}"`
						) || '',
					allSearchesName:
						oneitem.original.searchQuery.allSearches.map(
							(oneitem) => `"${oneitem.name}"`
						) || '',
					anyKeywords: oneitem.original.searchQuery.anyKeywords.toString() || '',
					anySearches:
						oneitem.original.searchQuery.anySearches.map(
							(oneitem) => `"${oneitem.id}"`
						) || '',
					anySearchesName:
						oneitem.original.searchQuery.anySearches.map(
							(oneitem) => `"${oneitem.name}"`
						) || '',
					notKeywords: oneitem.original.searchQuery.notKeywords.toString() || '',
					notSearches:
						oneitem.original.searchQuery.notSearches.map(
							(oneitem) => `"${oneitem.id}"`
						) || '',
					notSearchesName:
						oneitem.original.searchQuery.notSearches.map(
							(oneitem) => `"${oneitem.name}"`
						) || '',
					searchQueryType: oneitem.original.searchQuery.searchQueryType || '',
				};

				finalexport.push(response);
			} catch (error) {
				finalexport.push({ status: 'fail' });
				console.log(`FAIL`, error);
			}
		}

		setValidating(false);
		stopValidationRef.current = false;

		const data = {
			data: finalexport,
			delimiter: ',',
			filename: `Validation for ${companyId}`,
		};

		csvDownload(data);
	};

	const exportToWord = (rows) => {
		const content = `<html>
    <head>
      <style>
      table {
       border-collapse: collapse;
       width: 100%;
     }
     th, td {
       border: 1px solid #dddddd;
       text-align: left;
       padding: 8px;
       word-wrap: break-word; /* Added property for vertical growth */
     }
     th {
       font-weight: bold;
       background-color: teal; /* Set background color for header */
       color: white; /* Set text color for header */
     }
      </style>
    </head>
    <Body>
    <h1>Meltwater Search Booleans</h1>
    <hr/>
    <table>
    <tr>  
    <th>id</th>
    <th>Name</th>
    <th>Query</th>
    
    <th>Infotype</th>
    </tr>
      ${rows.map((item) => getOneSearch(item.original)).join('')}
      </table>
    </Body>
    </html>
    `;

		// Add a header and a line break between each item
		// Create a Blob containing the text content
		const blob = new Blob([content], { type: 'application/msword' });

		// Save the Blob as a Word document
		const filename = `Searches for ${companyId}`;
		saveAs(blob, filename);
	};

	const handleExportRows = (rows) => {
		// const errortab= props.tabledata.filter()
		const exportdata = rows.map((oneitem) => {
			//console.log("ONE ITEM ",oneitem.searchQuery.allKeywords)

			let updated = new Date(oneitem.original.updatedAt);
			if (oneitem.original.type !== 'explore') {
				oneitem.original.searchQuery.filter = [];
				oneitem.original.searchQuery.filter.id = 0;
				oneitem.original.searchQuery.filter.name = '';
				oneitem.original.searchQuery.filter.filters = {
					country: [],
					sentiment: [],
					language: [],
					sourceType: [],
					subKeyword: [],
					newsSource: [],
					rssSource: [],
					geoname: [],
					authorList: [],
					customCategory: [],
					newsReach: [],
					newsPremiumTypes: [],
					newsOutletTypes: [],
					broadcastSubtypes: [],
					booleans: [],
					newsMediaFormats: [],
					twitter: {
						accountStatus: [],
						followers: {
							minimum: null,
							maximum: null,
						},
						gender: [],
						tweetType: [],
						retweets: {
							minimum: null,
							maximum: null,
						},
						replies: {
							minimum: null,
							maximum: null,
						},
						likes: {
							minimum: null,
							maximum: null,
						},
					},
				};
			}

			let response = {
				name: oneitem.original.name,
				Search: oneitem.original.id,
				caseSensitive: oneitem.original.searchQuery.caseSensitive,
				Labels: oneitem.original.labelNames,
				FilterID: oneitem.original.searchQuery.filter.id,
				FilterName: oneitem.original.searchQuery.filter.name,
				SourceTypes: oneitem.original.searchQuery.filter.filters.sourceType,
				country: oneitem.original.searchQuery.filter.filters.country,
				language: oneitem.original.searchQuery.filter.filters.language,
				sentiment: oneitem.original.searchQuery.filter.filters.sentiment,

				newsSource: oneitem.original.searchQuery.filter.filters.newsSource.map(
					(oneitem) => {
						return `"${oneitem.id}"`;
					}
				),
				customCategory:
					oneitem.original.searchQuery.filter.filters.customCategory.map(
						(oneitem) => {
							return `"${oneitem.id}-${oneitem.name}"`;
						}
					),

				newsReach: oneitem.original.searchQuery.filter.filters.newsReach.map(
					(oneitem) => {
						return `"${oneitem.id}"`;
					}
				),
				newsOutletTypes:
					oneitem.original.searchQuery.filter.filters.newsOutletTypes.map(
						(oneitem) => {
							return `"${oneitem.id}"`;
						}
					),
				newsMediaFormats:
					oneitem.original.searchQuery.filter.filters.newsMediaFormats.map(
						(oneitem) => {
							return `"${oneitem.id}"`;
						}
					),

				rssSource: oneitem.original.searchQuery.filter.filters.rssSource.map(
					(oneitem) => {
						return `"${oneitem.id}"`;
					}
				),

				geoname: oneitem.original.searchQuery.filter.filters.geoname,
				authorList: oneitem.original.searchQuery.filter.filters.authorList,

				newsPremiumTypes:
					oneitem.original.searchQuery.filter.filters.newsPremiumTypes,

				broadcastSubtypes:
					oneitem.original.searchQuery.filter.filters.broadcastSubtypes,
				booleans: oneitem.original.searchQuery.filter.filters.booleans,

				booleanQuery:
					oneitem.original.searchQuery.booleanQuery !== undefined
						? oneitem.original.searchQuery.booleanQuery
						: '',
				updatedAt: updated,
				updatedBy: oneitem.original.updatedBy,
				updatedByUser:
					users.find((item) => item.user._id === oneitem.original.updatedBy)?.user
						.email || 'not found',
				createdAt: new Date(oneitem.original.createdAt),
				createdBy: oneitem.original.createdBy,
				createdByUser:
					users.find((item) => item.user._id === oneitem.original.createdBy)?.user
						.email || 'not found',

				allKeywords:
					oneitem.original.searchQuery.allKeywords !== undefined
						? oneitem.original.searchQuery.allKeywords.toString()
						: '',

				anyKeywords:
					oneitem.original.searchQuery.anyKeywords !== undefined
						? oneitem.original.searchQuery.anyKeywords.toString()
						: '',
				notKeywords:
					oneitem.original.searchQuery.notKeywords !== undefined
						? oneitem.original.searchQuery.notKeywords.toString()
						: '',
				allSearches: oneitem.original.searchQuery.allSearches.map((oneitem) => {
					return `"${oneitem.id}"`;
				}),
				allSearchesName: oneitem.original.searchQuery.allSearches.map((oneitem) => {
					return `${oneitem.name}`;
				}),
				anySearches: oneitem.original.searchQuery.anySearches.map((oneitem) => {
					return `"${oneitem.id}"`;
				}),
				anySearchesName: oneitem.original.searchQuery.anySearches.map((oneitem) => {
					return `${oneitem.name}`;
				}),
				notSearches: oneitem.original.searchQuery.notSearches.map((oneitem) => {
					return `"${oneitem.id}"`;
				}),

				notSearchesName: oneitem.original.searchQuery.notSearches.map((oneitem) => {
					return `"${oneitem.name}"`;
				}),
			};
			return response;
		});
		const data = {
			data: exportdata,
			delimiter: ',',
			filename: `Searches for ${companyId}`,
		};

		csvDownload(data);
	};

	/**
	 * "Export All" for the whole company runs as a job on the server: it builds
	 * the CSV from every search there and hands back a short-lived S3 link, so a
	 * large tenant's export no longer depends on the browser holding every search
	 * in memory.
	 *
	 * A filtered table is still exported client-side. The table now holds one
	 * page at a time, so that export covers the matches on the current page only
	 * — which is exactly what the user is looking at, and the snackbar says so.
	 */
	const handleExportAll = async (table) => {
		const { columnFilters, globalFilter } = table.getState();
		const isFiltered = (columnFilters?.length ?? 0) > 0 || Boolean(globalFilter);

		if (isFiltered) {
			const filteredRows = table.getPrePaginationRowModel().rows;
			handleExportRows(filteredRows);
			setFeedbackMsg(
				`Exported the ${filteredRows.length} filtered row(s) on this page. Clear the filter to export every search in the company.`
			);
			setSeverity('info');
			setOpenSnackBar(true);
			return;
		}

		const controller = new AbortController();
		exportAbortRef.current = controller;
		setExporting(true);
		setExportMessage('Starting export...');

		try {
			const result = await SearchService.runSearchesExport(companyId, {
				signal: controller.signal,
				onStatus: ({ status, searchCount }) => {
					setExportMessage(
						status === 'done'
							? 'Preparing download...'
							: `Building the CSV${searchCount ? ` (${searchCount} searches)` : ''}...`
					);
				},
			});

			if (result.empty) {
				setFeedbackMsg('This company has no searches, so there is nothing to export.');
				setSeverity('info');
				setOpenSnackBar(true);
				return;
			}

			const countSuffix = result.searchCount ? ` (${result.searchCount} searches)` : '';
			setFeedbackMsg(
				result.warning || `Downloading ${result.fileName}${countSuffix}.`
			);
			setSeverity(result.truncated ? 'warning' : 'success');
			setOpenSnackBar(true);

			// Navigate to the presigned link so the browser downloads it natively.
			// Fetching it into a blob would pull the whole file through the tab —
			// the cost the presigned link exists to avoid.
			if (typeof result.downloadUrl === 'string' && result.downloadUrl.startsWith('https://')) {
				window.location.assign(result.downloadUrl);
			} else {
				throw new Error('The export returned an invalid download link.');
			}
		} catch (error) {
			// An abort is the user leaving the page, not a failure worth reporting.
			if (controller.signal.aborted) {
				return;
			}
			console.error('Error exporting searches:', error);
			setFeedbackMsg(`Error exporting searches: ${error.message}`);
			setSeverity('error');
			setOpenSnackBar(true);
		} finally {
			if (exportAbortRef.current === controller) {
				exportAbortRef.current = null;
			}
			setExporting(false);
			setExportMessage('');
		}
	};

	// Stop polling if the user navigates away mid-export. The job itself carries
	// on server-side, so re-triggering later picks up a finished file.
	useEffect(() => () => exportAbortRef.current?.abort(), []);

	useEffect(() => {
		const jwtToken = getJwtToken();

		if (jwtToken) {
			const { permissions } = jwt_decode(jwtToken);
			setPermssions(permissions);
		}
	}, []);
	
	// Back to the first page against the server. The selection is dropped with
	// it: the rows behind it may no longer exist, or may have moved pages.
	const refreshData = () => {
		setRowSelection({});
		setSelectedSearches([]);
		refreshSearches();
		setFeedbackMsg('Refreshing searches...');
		setSeverity('info');
		setOpenSnackBar(true);
	};
	
	if (!permissions || !permissions.includes('read')) {
		// If user does not have permission, you can render a message, redirect, or display a different component
		return <div>You do not have permission to access this page.</div>;
	}
	
	function getMessage(count) {
		const guesstime = count * 1.5;
		let timemessage;
		if (guesstime < 60) {
			timemessage = 'under a minute';
		} else {
			const newtime = Math.round(guesstime / 60) + 1;
			timemessage = `${newtime} minutes`;
		}

		const retmessage = (
			<FluxList>
				<FluxListSubheader>
					{`Validation should take ${timemessage}`}
				</FluxListSubheader>
				<FluxListItem>
					{`Are you sure you want to perform this action?`}
				</FluxListItem>
			</FluxList>
		);
		return retmessage;
	}

	// Custom toolbar actions for the DataTable
	const renderToolbarActions = ({ table }) => {
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<FluxButton
					style={{ margin: 2 }}
					tint='primary'
					type='filled'
					onClick={() => handleExportAll(table)}
					disabled={exporting}
				>
					{exporting ? 'Exporting...' : 'Export All'}
				</FluxButton>
				{exporting && <span className={styles.exportStatus}>{exportMessage}</span>}
				{selectedRows.length > 0 && (
					<span className={styles.selectionCount}>
						{`${selectedRows.length} selected`}
					</span>
				)}

				<FluxButton
					style={{ margin: 2 }}
					tint='accent'
					type='filled'
					onClick={() => handleExportRows(selectedRows)}
					disabled={selectedRows.length === 0}
				>
					Export Selected
				</FluxButton>
				<FluxButton
					style={{ margin: 2 }}
					tint='accent'
					type='filled'
					onClick={() => exportToWord(selectedRows)}
					disabled={selectedRows.length === 0}
				>
					Export Word
				</FluxButton>
				{permissions.includes('read') && (
					<>
						<FluxButton
							style={{ margin: 2 }}
							tint='accent'
							type='filled'
							disabled={selectedRows.length === 0}
							onClick={handleOpenDialog}
						>
							Run Validation
						</FluxButton>
						<FluxButton
							style={{ margin: 2 }}
							tint='secondary'
							type='filled'
							onClick={refreshData}
						>
							Refresh Data
						</FluxButton>
						<ConfirmationDialog
							open={openDialog}
							handleClose={handleCloseDialog}
							handleConfirm={handleConfirmAction}
							title='Confirmation'
							message={getMessage(selectedRows.length)}
							confirmParams={selectedRows}
						/>
					</>
				)}
			</Stack>
			);
	};    return (
        <div className={styles.Search}>
            {validating && (
                <div className={styles.validationProgress}>
                    <FluxCircularProgress
                        percent={progress}
                    >{`Validating  ${progress}% `}</FluxCircularProgress>
                    <FluxButton type='outline' tint='accent' onClick={handleStopValidation}>
                        Stop
                    </FluxButton>
                </div>
            )}
            
            <DataTable 
                columns={columns}
                data={searchdata}
                isLoading={isLoading && !hasError}
                loadingMessage={'Loading searches...'}
                hasError={hasError}
                errorMessage={errorMessage}
                errorTitle="Error Loading Searches"
                onBackClick={() => setActiveTab(null)}
				renderToolbarActions={renderToolbarActions}
				backTooltip="Return to Dashboard"
				showGuide={false}
				guideSteps={searchGuideSteps}
				snackbarProps={{
					open: openSnackBar,
					onClose: handleCloseSnackBar,
					message: feedbackMsg,
					severity: severity,
					autoHideDuration: 10000
				}}
				tableProps={{
					// Server-side paging: the table asks for a page, useSearchPages
					// fetches exactly that page.
					manualPagination: true,
					rowCount: rowCount,
					onPaginationChange: onPaginationChange,
					// Selection has to key off the search id: row indexes are reused by
					// every page, so they would carry a selection across pages.
					getRowId: (row) => String(row.id),
					onRowSelectionChange: setRowSelection,
					state: {
						pagination,
						rowSelection,
						// The first load blanks the table; later pages keep the current
						// rows under a progress bar.
						showProgressBars: isFetching && hasLoaded,
					},
					muiTablePaginationProps: {
						rowsPerPageOptions: pageSizeOptions,
						showFirstButton: true,
						// The endpoint reports no total, so there is no last page to jump
						// to and the count is only known once the end is reached.
						showLastButton: false,
						labelDisplayedRows: ({ from, to }) =>
							hasMore ? `${from}-${to} of many` : `${from}-${to} of ${to}`,
					},
					muiSearchTextFieldProps: {
						// Filtering runs over the rows in the browser, which is now one
						// page — say so rather than implying a company-wide search.
						placeholder: 'Filter this page...',
						variant: 'standard',
						sx: { width: '300px' }
					},
					initialState: {
						density: 'compact',
						columnVisibility: {
							Boolean: false,
							'Custom Category': false,
							Language: false,
							AnySearches: false,
							AllSearches: false,
							NotSearches: false,
							Country: false,
							Query: false,
							FilterName: false,
							FilterID: false,
						},
					}
				}}
			/>
		</div>
	);
};

export default Search;
